import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, CircularProgress, Tab, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  commentsApi,
  fetchPageModel,
  fetchPrelemDefaultMeta,
  fetchResetData,
  getRequestFromDelivery,
  publishPageModel,
  savePageModel,
  schedulePublish,
  scheduleUnpublish,
  updatePrelemData,
  useWorkflow,
} from "@platformx/authoring-apis";
import {
  RootState,
  callSaveandResetWarning,
  copyPrelem,
  deletePrelem,
  hidePrelem,
  movePrelem,
  resetPrelemContent,
  savePrelemPosition,
  setUpdatedContent,
  updateDynamicSchemaPrelemContent,
  updateEcommercePrelemQueryParam,
  updateFeatureBoxServiceCardContent,
  updateImageVideoGalleryContent,
  updateMultiSlotContent,
  updateMultiSlotPrelemContent,
  updatePageModelModificationDate,
  updateSaveWarning,
} from "@platformx/authoring-state";
import { EditIcon, SettingIcon } from "@platformx/shared/static-assets";
import {
  CATEGORY_PAGE,
  CommonPlateformXDialog,
  Loader,
  PROJECT_CATCH_ERROR_LIST,
  PlatFormXDateTimeFormat,
  ShowToastError,
  ShowToastSuccess,
  appendBrandNameIfPresent,
  formatAddPrelem,
  getCurrentLang,
  getSelectedSite,
  safeParse,
  uriToJSON,
  useAccess,
  usePlatformAnalytics,
  useUserSession,
  workflowKeys,
} from "@platformx/utilities";
import { addMinutes } from "date-fns";
import {
  Suspense,
  SyntheticEvent,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import Prelem from "../AddPrelem/Prelem";
import Header from "../EditPageHeader/Header";
import HeaderMobile from "../EditPageHeader/HeaderMobile";
import LeftBox from "../PageContainer/LeftBox";
import PageLayout from "../PageContainer/PageContainer";
import RightBox from "../PageContainer/RightBox";
import PageSettingList from "../PageSettingList/PageSettingList";
import Analytics from "../PageSettings/Analytics";
import PageInfo from "../PageSettings/PageInfo";
import SEOBasics from "../PageSettings/SEOBasics";
import Schedule from "../PageSettings/Schedule";
import SocialShare from "../PageSettings/SocialShare";
import PrelemSettingMenu from "../PrelemSettingMenu/PrelemSettingMenu";
import PrelemInfo from "../PrelemSettings/PrelemInfo";
import PrelemSettingsCard from "../PrelemSettings/PrelemSettingsCard";
import EditPageIcons from "../utils/EditPageIcons";
import {
  DPWORLDS_TOUR_PRELEMS,
  ECOM_PRELEM_LIST,
  MULTISLOT_PRELEM_MANUAL_SELECT,
  PageSettingListData,
  PrelemActions,
  PrelemSettingCardList,
  StatusKey,
  isGalleryContentTypeCheck,
} from "../utils/constants";
import { PrelemInstance } from "../utils/editTypes";
import { consolidatePageModel } from "../utils/helper";
import { Dashboard_Keys } from "../utils/types";
import { useStyles } from "./EditPageContainer.styles";
import { doneInsituEditing, initInsituEditing } from "./insituEditing";

// Lazy load heavy components that are conditionally rendered
const EcommerceAuthoring = lazy(
  () => import("../../components/EcommerceAuthoring/EcommerceAuthoring"),
);
const ContentGallery = lazy(() =>
  import("@platformx/authoring-utilities").then((module) => ({ default: module.ContentGallery })),
);
const DynamicContentGallery = lazy(() =>
  import("@platformx/authoring-utilities").then((module) => ({
    default: module.DynamicContentGallery,
  })),
);
const WorkflowHistory = lazy(() =>
  import("@platformx/workflow-management").then((module) => ({ default: module.WorkflowHistory })),
);
const CustomContentGallery = lazy(() => import("../ETGCustomDialog/CustomContentGallery"));

// Loading fallback for lazy components
const LazyFallback = () => (
  <Box
    sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, minHeight: 200 }}>
    <CircularProgress size={32} />
  </Box>
);

const EditPageContainer = () => {
  const dispatch = useDispatch();
  const [pageId, setPageId] = useState("pageSetting");
  const classes = useStyles();
  const theme = useTheme();
  const [previewType, setPreviewType] = useState("web");
  const [prelemIndex, setPrelemIndex] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [getSession] = useUserSession();
  const { role, userInfo } = getSession();
  const { page } = useSelector((state: RootState) => state);
  const [searchParams] = useSearchParams();
  const client = useApolloClient();
  const [data, setData] = useState(page?.prelemMetaArray);
  const [workflow, setWorkflow] = useState({});
  const [currentPrelemIndx, setCurrentPrelemIndx] = useState(-1);
  const prelemEditRef = useRef<any>();
  const [selectedPrelemEditMode, setSelectedPrelemEditMode] = useState(-1);
  const [selectedPrelemEditState, setSelectedPrelemEditState] = useState(false);
  const [prelemDataReset, setPrelemDataReset] = useState<boolean>(false);
  const [contentGalleryStatus, setContentGalleryStatus] = useState(false);
  const [customContentGalleryStatus, setCustomContentGalleryStatus] = useState(false);
  const [fromPageContentType, setFromPageContentType] = useState("");
  const [insituEditingContentIndex, setInsituEditingContentIndex] = useState("");
  const [editableValue, setEditableValue] = useState<any>(null);
  const [prelemResetIndex, setPrelemResetIndex] = useState<number>(-1);
  const imageSettingRef = useRef<any[]>([]);
  const [isResetPopup, setIsResetPopup] = useState(false);
  const [isDeletePopup, setIsDeletePopup] = useState(false);
  const [showSaveWarning, setShowSaveWarning] = useState<boolean>(false);
  const insituContentIndex = useRef(0);
  const [triggerCase, setTriggerCase] = useState<string | null>(null);
  const selectedTab = useRef("image");
  const selectImageVideoGalleryPrelem = useRef();
  const isPageMounted = useRef(false);
  const selectedSlot = useRef(-1);
  const saveStatus = useRef(true);
  const contentUpdateStatus = useRef(false);
  const selectedContentEditorialPath = useRef<any>("");
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(true);
  const [handleImpression] = usePlatformAnalytics();
  const [loader, setLoader] = useState<boolean>(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [mutatePrelemContentQuery] = useMutation(updatePrelemData);
  const [fetchDefaultData] = useLazyQuery(fetchResetData);
  const [fetchPrelemDefaultInfo] = useLazyQuery(fetchPrelemDefaultMeta);
  const [mutatePublish] = useMutation(publishPageModel);
  const [mutateSchedulePublish] = useMutation(schedulePublish);
  const [mutate] = useMutation(savePageModel);
  const [gifPlaying, setGifPlaying] = useState(false);
  const { workflowRequest } = useWorkflow();
  const [enableWorkflowHistory, setEnableWorkflowHistory] = useState<boolean>(false);
  const username = `${userInfo.first_name} ${userInfo.last_name}`;
  const login_user_id = userInfo?.user_id;
  const [timerState, setTimerState] = useState(localStorage.getItem("pageTimerState") === "true");
  const isFrombuttonforEcommerce = useRef(false); // Making this false so that ecom dynamic prelem works as expected.
  const publishPopup = useRef({
    publishTitle: "Congratulations",
    publishDescription:
      "Your page has been sent for publishing & will be published in a few seconds.",
    publishCloseText: "View",
    publishConfirmText: "Go To Pages",
  });
  const [value, setValue] = useState("Page_Setting");
  // Single media query for mobile/tablet breakpoint (used for both tab switching and layout)
  const isMobileView = useMediaQuery(`@media(max-width:${theme?.breakpoints?.values?.sm}px)`);
  const { comments } = useSelector((state: RootState) => state.comment.commentInfo);
  const [mutateScheduleUnPublish] = useMutation(scheduleUnpublish);
  const [openPublishModal, setOpenPublishModal] = useState<boolean>(false);
  const [showWorkflowSubmit, setShowWorkflowSubmit] = useState<boolean>(false);
  const { canAccessAction } = useAccess();
  const isPreviewPage = searchParams.get("preview") || false;
  const [saveDone, setSaveDone] = useState(false);
  const handleChangeTab = (_: SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const getPageModel = async () => {
    try {
      setLoader(true);
      await fetchPageModel(dispatch, client, searchParams?.get("page"), navigate);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    setValue(isMobileView ? "Page_Design" : "Page_Setting");
  }, [isMobileView]);

  useEffect(() => {
    if (timerState) {
      localStorage.setItem("pageTimerState", "true");
    }
  }, [timerState]);

  useEffect(() => {
    if (
      Object.keys(page?.pageModel).length === 0 ||
      (searchParams?.get("page") && searchParams?.get("page") !== page?.pageModel?.Path)
    ) {
      getPageModel();
    } else {
      const {
        Title,
        Path,
        workflow_status,
        stages,
        Page_LastModifiedBy,
        Page_CreatedBy,
        Page,
        task_status,
        user_id,
        user_name,
      } = page.pageModel;
      setWorkflow({
        title: Title,
        description: "",
        path: Path,
        workflow_status,
        stages,
        tag_name: Dashboard_Keys.SITE_PAGE,
        last_modifiedBy: Page_LastModifiedBy,
        createdBy: Page_CreatedBy,
        role,
        page: Page,
        enable: !!stages?.length,
        login_user_id,
        task_status,
        task_user_id: user_id,
        task_user_name: user_name,
      });
    }
  }, [page?.pageModel]);

  useEffect(() => {
    if (isPageMounted && page?.showSaveWarning) {
      saveStatus.current = false;
    }
  }, [page?.showSaveWarning]);

  const publishUnpublishFieldCheck = (
    IsSchedulePublish,
    IsScheduleUnpublish,
    SchedulePublishDateTime,
    ScheduleUnpublishDateTime,
  ) => {
    if (IsSchedulePublish && !SchedulePublishDateTime) {
      return false;
    }
    if (IsScheduleUnpublish && !ScheduleUnpublishDateTime) {
      return false;
    }
    return true;
  };
  const workflowSubmitRequest = async (workflowObj, status) => {
    const { success, workflow_status } = await workflowRequest(workflowObj, status);
    if (success) {
      workflow_status === workflowKeys.publish.toLowerCase() && status === workflowKeys.approve
        ? setOpenPublishModal(true)
        : setShowWorkflowSubmit(true);
    }
  };
  const savePage = useCallback(
    async (isWorkflow = false, props = {}, event_step = "", publishCall = false) => {
      try {
        setGifPlaying(true);
        const {
          IsSchedulePublish,
          IsScheduleUnpublish,
          SchedulePublishDateTime,
          ScheduleUnpublishDateTime,
        } = page.pageSettings;
        if (
          publishUnpublishFieldCheck(
            IsSchedulePublish,
            IsScheduleUnpublish,
            SchedulePublishDateTime,
            ScheduleUnpublishDateTime,
          )
        ) {
          if (page?.callSave === true) {
            dispatch(callSaveandResetWarning(false));
          } else {
            dispatch(updateSaveWarning(false));
          }
          saveStatus.current = true;
          if (showSaveWarning) {
            setShowSaveWarning(false);
          }
          const newModel = consolidatePageModel(
            page?.pageModel,
            page?.prelemMetaArray,
            page?.pageSettings,
            username,
          );
          dispatch(updatePageModelModificationDate(new Date()));
          try {
            await mutate({
              variables: { input: appendBrandNameIfPresent(newModel) },
              context: {
                headers: {
                  sitename: getSelectedSite(),
                },
              },
            })
              .then(() => {
                setTimerState(true);

                if (isWorkflow) {
                  workflowSubmitRequest(props, event_step);
                } else {
                  !publishCall && ShowToastSuccess(t("page_save_toast"));
                }
                const pageDataObj = {
                  eventType: "Page Saved",
                  pageSaved: true,
                };
                handleImpression(pageDataObj.eventType, pageDataObj);
              })
              .catch((error) => {
                console.error(`${PROJECT_CATCH_ERROR_LIST.site_setting}:savePage`, error);
                setTimerState(false);
                ShowToastError(`${t("page_save_error")}`);
                // throw error;
              });
          } catch (error) {
            console.error(error, "Error in savePage");
          }
        } else {
          setTimerState(false);
          ShowToastError(`${t("publish_or_unpublish")}`);
        }
      } catch (error) {
        console.error("An error occurred while saving the page:", error);
        throw error; // Rethrow the error for higher-level handling
      } finally {
        setGifPlaying(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, showSaveWarning, username],
  );

  useEffect(() => {
    if (isPageMounted.current && page?.callSave === true) {
      savePage();
    }
  }, [page?.callSave]);

  useEffect(() => {
    isPageMounted.current = true;
  }, []);

  useEffect(() => {
    setData(page?.prelemMetaArray);
  }, [page?.prelemMetaArray]);

  const updatePrelemContent = (
    updatedContent,
    prelemAt,
    documentPath,
    documentCreationPath,
    documentType,
    instanceId,
  ) => {
    setIsSaveButtonEnabled(false);
    mutatePrelemContentQuery({
      variables: {
        input: updatedContent,
        docPath: documentPath,
        docCreationPath: documentCreationPath,
        docType: documentType,
        instanceId: instanceId,
      },
    })
      .then((res) => {
        dispatch(
          setUpdatedContent({
            path: res.data.authoring_createOrUpdatePrelemContent.path,
            updatedContent: updatedContent,
            prelemIndex: prelemAt,
          }),
        );
        setIsSaveButtonEnabled(true);
        setSelectedPrelemEditState(false);
        setSaveDone(false);
      })
      .catch((error) => {
        ShowToastError(error?.graphQLErrors[0]?.message);
        setSelectedPrelemEditState(false);
        setSaveDone(false);
        console.error(JSON.stringify(error, null, 2));
      });
  };

  const compareUpdatedInsituEditingValue = (obj1, obj2) => {
    for (const key in obj2) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (
      !compareUpdatedInsituEditingValue(
        page?.prelemMetaArray[insituEditingContentIndex]?.content,
        editableValue?.updatedEditableValue,
      ) ||
      contentUpdateStatus?.current
    ) {
      if (contentUpdateStatus?.current) {
        contentUpdateStatus.current = false;
      }
      if (!editableValue?.updatedEditableList?.some((ele) => ele?.errorString)) {
        if (editableValue && insituEditingContentIndex !== "") {
          updatePrelemContent(
            {
              ...page?.prelemMetaArray[insituEditingContentIndex]?.content,
              ...editableValue.updatedEditableValue,
            },
            insituEditingContentIndex,
            page?.prelemMetaArray?.[insituEditingContentIndex]?.DocumentPath,
            page?.prelemMetaArray?.[insituEditingContentIndex]?.DocumentCreationPath,
            page?.prelemMetaArray?.[insituEditingContentIndex]?.DocumentType,
            page?.prelemMetaArray?.[insituEditingContentIndex]?.InstanceId,
          );
        }
      }
    } else {
      setSelectedPrelemEditState(false);
    }
  }, [editableValue, insituEditingContentIndex]);

  useEffect(() => {
    const { prelemMetaArray = [], insertPrelemAt = 0, scrollIndex = 0 } = page;
    if (prelemMetaArray.length > 1 && scrollIndex !== 0) {
      document?.getElementById(`prelem${insertPrelemAt}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [page]);

  const handleChange = (_: SyntheticEvent, updatedVal: string) => {
    setPreviewType(updatedVal);
    handleScreenPreview(updatedVal);
  };

  // to open prelem settings section
  const openPrelemSettings = (prelemIndexInitial, operation) => {
    setPageId(operation);
    setPrelemIndex(prelemIndexInitial);
  };

  const scrollToTop = async (prelemRef) => {
    await prelemRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handlePrelemEditMode = async (prelemIndex1: number, prelemRef, schemaArray) => {
    dispatch(savePrelemPosition({ position: page?.insertPrelemAt, index: 0 }));
    prelemEditRef.current = prelemRef.current;
    await initInsituEditing(schemaArray, prelemRef);
    await setSelectedPrelemEditMode(prelemIndex1);
    await setSelectedPrelemEditState(true);
    await setPrelemDataReset(false);
    await scrollToTop(prelemRef);
    setSaveDone(false);
  };

  const handleCopyPrelem = async (prelemIndexInitial: number) => {
    try {
      const prelem = page?.prelemMetaArray[prelemIndexInitial];
      const prelemid = prelem?.PrelemId;
      const documentType = prelem?.DocumentType;
      // fetch reset/default data to obtain a fresh DocumentPath
      try {
        const response = await fetchDefaultData({
          variables: { input: documentType, prelemId: prelemid },
        });
        if (response) {
          const prelemAfterReset = response?.data?.authoring_resetContent;
          const newDocumentPath = prelemAfterReset?.DocumentPath;

          const duplicatedPrelem = safeParse(JSON.stringify(prelem));
          // JSON.parse(JSON.stringify(prelem));
          if (newDocumentPath && duplicatedPrelem) {
            duplicatedPrelem.DocumentPath = newDocumentPath;
          }

          // Dispatch copy with the new document path so duplicate points to a distinct document
          dispatch(copyPrelem({ prelemIndex: prelemIndexInitial, newDocumentPath }));
          // Create the prelem content at the new path so backend has an independent document
          updatePrelemContent(
            duplicatedPrelem.content,
            prelemIndexInitial + 1, // duplicated prelem is inserted at index prelemIndexInitial + 1
            newDocumentPath,
            duplicatedPrelem.DocumentCreationPath,
            duplicatedPrelem.DocumentType,
            duplicatedPrelem.InstanceId,
          );
        }
      } catch (error) {
        ShowToastError(t("prelem_duplicate_error_toast"));
        console.error(error, "Error in handleCopyPrelem - fetchDefaultData");
      }
    } catch (error) {
      console.error(error, "Error in handleCopyPrelem");
      ShowToastError(t("prelem_duplicate_error_toast"));
      // dispatch(copyPrelem(prelemIndexInitial)); // fallback: perform legacy copy without new document path
    }
  };

  const onToggleContentGallery = (
    activeTab?: string,
    imageVideoContentGallery?: any,
    slotNumber?: number,
  ) => {
    selectedTab.current = activeTab || "";
    selectImageVideoGalleryPrelem.current = imageVideoContentGallery;
    if (slotNumber !== -1 && slotNumber !== undefined) selectedSlot.current = slotNumber;
    setContentGalleryStatus(!contentGalleryStatus);
    setFromPageContentType("");
  };

  const onToggleCustomContent = (isOpen?: boolean) => {
    if (typeof isOpen === "boolean") {
      setCustomContentGalleryStatus(isOpen);
      return;
    }
    setCustomContentGalleryStatus((prev) => !prev);
  };

  const handleSetSelectedPrelemEditState = (
    schemaArray,
    prelemRef,
    index,
    buttonsKeysPopulatedObj,
  ) => {
    setPageId("pageSetting");
    dispatch(updateSaveWarning(true));
    setInsituEditingContentIndex(index);
    insituContentIndex.current = index;
    const editableValueNotification = doneInsituEditing(
      schemaArray,
      prelemRef,
      buttonsKeysPopulatedObj,
      page?.prelemMetaArray[insituContentIndex?.current]?.PrelemId,
    );
    setEditableValue(editableValueNotification);
    saveStatus.current = false;
    editableValueNotification?.updatedEditableList.forEach((ele) => {
      if (ele?.errorString) {
        ShowToastError(`${ele.errorString}error with ${ele.id} field`);
        setSaveDone(false);
      }
    });
  };

  const onOpenContentType = () => {
    setFromPageContentType("content");
    setContentGalleryStatus(!contentGalleryStatus);
  };

  const eComContentGalleryHandle = (data1: string, fromButton = true, selectedMultiSlot = -1) => {
    selectedSlot.current = selectedMultiSlot;
    isFrombuttonforEcommerce.current = fromButton;
    if (data1 && typeof data1 === "string") {
      if (data1.includes("ecomEnCodeParse") || !fromButton) {
        setFromPageContentType(uriToJSON(data1));
      } else {
        setFromPageContentType("");
      }
    } else {
      setFromPageContentType("");
    }
    setContentGalleryStatus(!contentGalleryStatus);
  };
  /**
   * get params from contentType eCom component
   * @param data string
   */
  const ecomDoneClick = (data2: string) => {
    setFromPageContentType("");
    setContentGalleryStatus(!contentGalleryStatus);
    if (!isFrombuttonforEcommerce.current) {
      contentUpdateStatus.current = true;
      dispatch(
        updateEcommercePrelemQueryParam({
          data: data2,
          prelemIndex: selectedPrelemEditMode,
          selectedSlot: selectedSlot.current,
        }),
      );
    } else {
      selectedContentEditorialPath.current = data2;
    }
  };

  const handleSelectedDynamicSchemaContent = (contentObj, dynamicPrelemType) => {
    contentUpdateStatus.current = true;
    dispatch(
      updateDynamicSchemaPrelemContent({
        contentObj,
        prelemIndex: selectedPrelemEditMode,
        dynamicPrelemType,
      }),
    );
    setContentGalleryStatus(!contentGalleryStatus);
    setFromPageContentType("");
  };

  const handleSelectedMultiSlotContent = (contentObj) => {
    contentUpdateStatus.current = true;
    dispatch(
      updateMultiSlotPrelemContent({
        contentObj,
        prelemIndex: selectedPrelemEditMode,
        selectedSlot: selectedSlot.current,
      }),
    );
    setContentGalleryStatus(!contentGalleryStatus);
    setFromPageContentType("");
  };

  const handleSelectedContent = async (item: any) => {
    contentUpdateStatus.current = true;
    if (
      ["accolades", "servicecard", "testimonial", "faq"].includes(item?.ContentType?.toLowerCase())
    ) {
      dispatch(
        updateFeatureBoxServiceCardContent({
          slotContent: item,
          prelemIndex: selectedPrelemEditMode,
        }),
      );
    } else if (selectImageVideoGalleryPrelem.current) {
      let updatedGalleryContent = [];
      try {
        //Currently we are not able to trigger this call so if in future something break we need to remove this multislot delivery call and have to use gateway api call
        await getRequestFromDelivery(
          `api/v1/web/en/delivery/multi-slot-content?path=${item?.EditorialItemPath}&contentType=${item?.ContentType}&documentType=${data[currentPrelemIndx].DocumentType}`,
        ).then((res) => {
          updatedGalleryContent = res?.data?.fetchMultiSlotContent?.Gallery;
        });
      } catch (error) {
        console.error(error, "Error in handleSelectedContent");
      }
      if (updatedGalleryContent?.length) {
        dispatch(
          updateImageVideoGalleryContent({
            content: updatedGalleryContent,
            selectedPrelemIndex: selectedPrelemEditMode,
            activeTab: selectedTab,
            EditorialItemPath: item?.EditorialItemPath,
          }),
        );
      }
    } else if (selectedSlot.current !== -1) {
      dispatch(
        updateMultiSlotContent({
          slotContent: item,
          currentSlot: selectedSlot.current,
          prelemIndex: selectedPrelemEditMode,
        }),
      );
    } else {
      const {
        Title = "",
        Author = "",
        ContentType = "",
        PublishedDate = "",
        CurrentPageURL = "",
        lastModifiedDate = "",
        EditorialItemPath = "",
      } = item;
      const isGalleryArray = isGalleryContentTypeCheck(ContentType); //contentType check
      // convertTo string
      const contentTypeObj = JSON.stringify({
        Title: Title,
        Author: Author,
        ContentType: ContentType,
        isGalleryArray: isGalleryArray,
        contentCreatedBy: PublishedDate,
        isQueryType: "ContentEnCodeParse",
        contentCreatedDate: lastModifiedDate,
        currentPath: isGalleryArray ? EditorialItemPath : CurrentPageURL, // condition based add endPath
      });
      selectedContentEditorialPath.current = contentTypeObj;
    }
    setContentGalleryStatus(!contentGalleryStatus);
    setFromPageContentType("");
  };

  const routeChange = (path = "/prelem-search") => {
    const addPrelemObj = {
      eventType: "Add Prelem",
      clickType: "Add Prelem click on edit page",
    };
    handleImpression(addPrelemObj.eventType, addPrelemObj);
    if (path === "prelem-search") {
      dispatch(dispatch(savePrelemPosition({ position: page?.insertPrelemAt, index: 0 })));
    }
    navigate(path);
  };

  const handleScreenPreview = (device) => {
    const previewObj = {
      eventType: "Page Preview",
      previewType: `${device} Preview`,
    };
    handleImpression(previewObj.eventType, previewObj);
    setPreviewType(device);
    navigate(`/preview-page/${device}`, {
      state: { pageName: page?.pageModel?.Page },
    });
  };

  const addSectionTouchPointClick = (prelemPosition: string, position: string) => {
    let prelemInsertIndexAt = 0;
    if (position === "top") prelemInsertIndexAt = parseInt(prelemPosition);
    if (position === "bottom") prelemInsertIndexAt = parseInt(prelemPosition) + 1;
    dispatch(savePrelemPosition({ position: prelemInsertIndexAt, index: prelemInsertIndexAt }));
    routeChange();
  };

  const handleOperations = (prelemIndexInitial, operation, prelemRef, schemaArray) => {
    setCurrentPrelemIndx(prelemIndexInitial);
    if (page?.scrollIndex !== 0) {
      dispatch(savePrelemPosition({ position: page?.insertPrelemAt, index: 0 }));
    }
    switch (operation) {
      case PrelemActions.PRELEM_INFO:
        setPageId(operation);
        setPrelemIndex(prelemIndexInitial);
        break;
      case PrelemActions.EDIT:
        handlePrelemEditMode(prelemIndexInitial, prelemRef, schemaArray);
        break;
      case PrelemActions.COPY:
        handleCopyPrelem(prelemIndexInitial);
        break;
      case PrelemActions.SHOW:
      case PrelemActions.HIDE:
        dispatch(hidePrelem(prelemIndexInitial));
        break;
      case PrelemActions.UP:
      case PrelemActions.DOWN:
        dispatch(movePrelem({ prelemIndex: prelemIndexInitial, operation: operation }));
        setPageId("pageSetting");
        break;
      case PrelemActions.RESET:
        setIsResetPopup(true);
        break;
      case PrelemActions.DELETE:
        setIsDeletePopup(true);
        break;
      case PrelemActions.PRELEM_SETTING:
        openPrelemSettings(prelemIndexInitial, operation);
        break;
      default:
        break;
    }
  };

  const resetCloseButtonHandle = () => {
    setIsResetPopup(false);
  };

  const prelemReset = async (prelemIndex1: number) => {
    const prelemid = page?.prelemMetaArray[prelemIndex1].PrelemId;
    const documentType = page?.prelemMetaArray[prelemIndex1].DocumentType;
    try {
      const [response1, response2] = await Promise.allSettled([
        fetchPrelemDefaultInfo({ variables: { prelemId: prelemid } }),
        fetchDefaultData({
          variables: { input: documentType, prelemId: prelemid },
        }),
      ]);
      let prelemAfterReset, prelemContent;
      if (response1.status === "fulfilled" && response2.status === "fulfilled") {
        prelemContent = formatAddPrelem(response1.value?.data.authoring_prelemById);
        prelemAfterReset = { ...response2.value?.data.authoring_resetContent };
        delete prelemContent.DocumentPath;
        dispatch(
          resetPrelemContent({
            contentFetched: prelemContent,
            prelemAfterReset,
            prelemAt: prelemIndex1,
          }),
        );
        ShowToastSuccess(t("prelem_reset_success_toast"));
      } else {
        ShowToastError(t("prelem_reset_error_toast"));
      }
      setPrelemDataReset(true);
      setPrelemResetIndex(prelemIndex1);
    } catch (error) {
      console.error(error, "Error in prelemReset");
    }
  };

  const resetConfirmButtonHandle = async () => {
    await prelemReset(currentPrelemIndx);
    setIsResetPopup(false);
  };

  const deleteConfirmButtonHandle = () => {
    dispatch(deletePrelem(currentPrelemIndx));
    setIsDeletePopup(false);
    ShowToastSuccess(t("prelem_deleted_toast"));
    setPageId("pageSetting");
  };
  const deleteCloseButtonHandle = () => {
    setIsDeletePopup(false);
  };

  const checkScheduleValidity = (scheduleType, publishtimeEntered, unpublishtimeEntered) => {
    const currentDateTime = addMinutes(new Date(), 5);

    switch (scheduleType) {
      case "PUBLISH":
        if (publishtimeEntered < currentDateTime) {
          setPublishLoading(false);
          ShowToastError(`${t("page")} ${t("publish_time_toast")}`);
          return false;
        }
        break;
      case "UNPUBLISH":
        if (unpublishtimeEntered < currentDateTime) {
          setPublishLoading(false);
          ShowToastError(`${t("page")} ${t("unpublish_time_toast")}`);
          return false;
        } else {
          if (page?.pageSettings.IsSchedulePublish && publishtimeEntered) {
            const validUnpublishDateTime = addMinutes(publishtimeEntered, 10);
            if (unpublishtimeEntered < validUnpublishDateTime) {
              setPublishLoading(false);
              ShowToastError(`${t("page")} ${t("publish_vs_unpublish_toast")}`);
              return false;
            } else {
              return true;
            }
          }
        }
        return true;
      default:
        break;
    }
    return true;
  };

  const scheduleUnPublishPageAt = (
    unpublishrequestdto,
    pageModel,
    scheduleTime,
    timeZone,
    validPublishTime,
    validUnpublishTime,
  ) => {
    dispatch(updatePageModelModificationDate(new Date()));
    mutateScheduleUnPublish({
      variables: {
        publishrequestdto: unpublishrequestdto,
        scheduleTime: scheduleTime,
        pageModelRequest: pageModel,
        timeZone: timeZone,
      },
    })
      .then(() => {
        setTimerState(true);
        if (validUnpublishTime && validPublishTime) {
          return;
        } else {
          if (validUnpublishTime) {
            publishPopup.current = {
              publishTitle: "Congratulations",
              publishDescription: `Your page has been sent for publishing & will be published in a few seconds and scheduled to unpublish at ${PlatFormXDateTimeFormat(
                page?.pageSettings?.ScheduleUnpublishDateTime,
              )}`,
              publishCloseText: "View",
              publishConfirmText: "Go To Pages",
            };
            setOpenPublishModal(true);
            setPublishLoading(false);
            ShowToastSuccess(`${t("page")} ${t("scheduled_to_unpublish_toast")}`);
          }
        }
      })
      .catch(() => {
        setPublishLoading(false);
        ShowToastError(`${t("page")} ${t("unpublish_error_toast")}`);
      });
  };

  const schedulePublishPageAt = (
    publishrequestdto,
    pageModel,
    scheduleTime,
    timeZone,
    validPublishTime,
    validUnpublishTime,
  ) => {
    dispatch(updatePageModelModificationDate(new Date()));
    mutateSchedulePublish({
      variables: {
        publishrequestdto: publishrequestdto,
        scheduleTime: scheduleTime,
        pageModelRequest: pageModel,
        timeZone: timeZone,
      },
    })
      .then(() => {
        setTimerState(true);
        if (validPublishTime && validUnpublishTime) {
          publishPopup.current = {
            publishTitle: "Congratulations",
            publishDescription: `Your page has been scheduled to publish at ${PlatFormXDateTimeFormat(
              page?.pageSettings?.SchedulePublishDateTime,
            )} & scheduled to unpublish at ${PlatFormXDateTimeFormat(
              page?.pageSettings?.ScheduleUnpublishDateTime,
            )}`,
            publishCloseText: "View",
            publishConfirmText: "Go To Pages",
          };
          setOpenPublishModal(true);
          setPublishLoading(false);
          ShowToastSuccess(`${t("page")} ${t("scheduled_to_unpublish_toast")}`);
          ShowToastSuccess(`${t("page")} ${t("scheduled_to_publish_toast")}`);
        } else {
          if (validPublishTime) {
            if (!page?.pageSettings.IsScheduleUnpublish) {
              publishPopup.current = {
                publishTitle: "Congratulations",
                publishDescription: `Your page has been scheduled to publish at ${PlatFormXDateTimeFormat(
                  page?.pageSettings?.SchedulePublishDateTime,
                )}`,
                publishCloseText: "View",
                publishConfirmText: "Go To Pages",
              };
              setOpenPublishModal(true);
              setPublishLoading(false);
              ShowToastSuccess(`${t("page")} ${t("scheduled_to_publish_toast")}`);
            }
          }
        }
      })
      .catch(() => {
        setPublishLoading(false);
        ShowToastError(`${t("page")} ${t("publish_error_toast")}`);
      });
  };

  const publishPageNow = (requestdto, pageModel, timeZone) => {
    dispatch(updatePageModelModificationDate(new Date()));
    mutatePublish({
      variables: {
        input: requestdto,
        pageModelRequest: pageModel,
        timeZone: timeZone,
      },
      context: {
        headers: {
          sitename: getSelectedSite(),
        },
      },
    })
      .then(() => {
        setTimerState(true);
        if (!page?.pageSettings.IsSchedulePublish && !page?.pageSettings.IsScheduleUnpublish) {
          publishPopup.current = {
            publishTitle: "Congratulations",
            publishDescription:
              "Your page has been sent for publishing & will be published in a few seconds.",
            publishCloseText: "View",
            publishConfirmText: "Go To Pages",
          };
          setOpenPublishModal(true);
          setPublishLoading(false);
        }
        if (showSaveWarning) {
          setShowSaveWarning(false);
        }

        const pageDataObj = {
          eventType: "Page Published",
          pagePublished: true,
        };
        handleImpression(pageDataObj.eventType, pageDataObj);
      })
      .catch(() => {
        setPublishLoading(false);
        ShowToastError(`${t("page")} ${t("published_error_toast")}`);
      });
  };

  const publishPage = () => {
    const schedulePublish1 = page?.pageSettings?.SchedulePublishDateTime;
    const scheduleUnpublish1 = page?.pageSettings?.ScheduleUnpublishDateTime;
    const newModel = consolidatePageModel(
      page?.pageModel,
      page?.prelemMetaArray,
      page?.pageSettings,
      username,
    );
    const requestdto = {
      page: page?.pageModel.Page,
      parentpageurl: "/",
      currentpageurl: page?.pageModel.CurrentPageURL,
    };
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const validPublishTime = page?.pageSettings.IsSchedulePublish
      ? checkScheduleValidity(
          StatusKey.publish.toUpperCase(),
          new Date(schedulePublish1),
          scheduleUnpublish1,
        )
      : false;
    const validUnpublishTime = page?.pageSettings.IsScheduleUnpublish
      ? checkScheduleValidity(
          StatusKey.unpublish.toUpperCase(),
          new Date(schedulePublish1),
          new Date(scheduleUnpublish1),
        )
      : false;
    if (page?.pageSettings.IsSchedulePublish && page?.pageSettings.IsScheduleUnpublish) {
      if (validPublishTime && validUnpublishTime) {
        schedulePublishPageAt(
          requestdto,
          newModel,
          schedulePublish1,
          timeZone,
          validPublishTime,
          validUnpublishTime,
        );
        scheduleUnPublishPageAt(
          requestdto,
          newModel,
          scheduleUnpublish1,
          timeZone,
          validPublishTime,
          validUnpublishTime,
        );
      }
    } else {
      if (page?.pageSettings.IsSchedulePublish && validPublishTime) {
        schedulePublishPageAt(
          requestdto,
          newModel,
          schedulePublish1,
          timeZone,
          validPublishTime,
          validUnpublishTime,
        );
      }
      if (page?.pageSettings.IsScheduleUnpublish && validUnpublishTime) {
        publishPageNow(requestdto, newModel, timeZone);
        scheduleUnPublishPageAt(
          requestdto,
          newModel,
          scheduleUnpublish1,
          timeZone,
          validPublishTime,
          validUnpublishTime,
        );
      }
      if (!page?.pageSettings.IsSchedulePublish && !page?.pageSettings.IsScheduleUnpublish) {
        publishPageNow(requestdto, newModel, timeZone);
      }
    }
    if (showSaveWarning) {
      setShowSaveWarning(false);
    }
  };

  const callFnsCase = async (triggerCaseSent) => {
    setShowSaveWarning(false);
    // Parse URL only when needed, not on every render
    const currentUrl = new URL(window.location.href);
    const searchCat = currentUrl.searchParams.get("searchCat");
    const searchTerm = currentUrl.searchParams.get("searchTerm");
    const sortBy = currentUrl.searchParams.get("sortBy");
    switch (triggerCaseSent) {
      case "PAGE_LIST":
        localStorage.removeItem("path");
        navigate({
          pathname: "/sitepage",
          search: `?${createSearchParams({
            ...(searchCat && {
              searchCat: searchCat ? searchCat.toString() : "",
            }),
            ...(searchTerm && {
              searchTerm: searchTerm ? searchTerm.toString() : "",
            }),
            ...(sortBy && { sortBy: sortBy ? sortBy.toString() : "" }),
          })}`,
        });
        break;
      case "PUBLISH":
        if (!saveStatus.current) {
          await savePage(false, {}, "", true);
        }
        publishPage();
        break;
      default:
        break;
    }
  };

  const saveCheck = () => {
    if (!saveStatus.current) {
      setShowSaveWarning(true);
      return false;
    }
    return true;
  };

  const handleBack = () => {
    const status = saveCheck();
    if (!status) {
      setTriggerCase("PAGE_LIST");
    } else {
      callFnsCase("PAGE_LIST");
    }
  };

  // const onCloseSaveWarningHandler = () => {
  //   savePage();
  //   setShowSaveWarning(false);
  // };

  const unsavedChangesCrossButtonHandle = () => {
    setShowSaveWarning(false);
    setTriggerCase(null);
  };

  const closeButtonHandle = () => {
    setOpenPublishModal(false);
    setShowWorkflowSubmit(false);
  };

  // const changePublishDialogState = () => {
  //   setOpenPublishModal(false);
  // };

  const onPublishClick = () => {
    setPublishLoading(true);
    callFnsCase("PUBLISH");
  };

  // const theme = useTheme();
  const mobileHeader = useMediaQuery(`@media(max-width:${theme?.breakpoints?.values?.em - 1}px)`);
  const webHeader = useMediaQuery(`@media(min-width:${theme?.breakpoints?.values?.em - 1}px)`);
  // const RightBoxWeb = useMediaQuery(theme.breakpoints.up("sm"));
  const arr = searchParams?.get("page")?.split("/") || [];
  const pathnm = `${arr[arr.length - 1]}`; //`${arr[6]}/${arr[7]}`;
  //create comment
  const createComment = () => {
    const currentLanguage = getCurrentLang();
    const createCommentRequest = {
      document_path: `/content/documents/hclplatformx/${currentLanguage}/page/${pathnm}`,
      status: false,
      document_type: "Page",
      created_by: username, //articleInstance.CommonFields.createdBy,
      last_modified_by: username,
      reviewer_comments: [comments],
    };

    return commentsApi.createOrUpdateComment({
      input: createCommentRequest,
    });
  };
  let count = 0;

  const memoizedProps = useMemo(
    () => ({
      lastmodifiedDate: page?.pageModel.Page_LastModificationDate,
      handleChange,
      value: previewType,
      handleBack,
      previewStatus: !(
        Object.values(page?.prelemMetaArray).every((v: any) => v.IsHidden) ||
        page?.prelemMetaArray.length === 0
      ),
      handleSaveClick: savePage,
      handlePublishClick: onPublishClick,
      workflow,
      timerState,
      prelemEditState: selectedPrelemEditState,
      gifPlaying,
      createComment,
      setEnableWorkflowHistory,
      iconDisabled: !saveStatus.current,
      loader,
    }),
    [
      page?.pageModel.Page_LastModificationDate,
      page?.prelemMetaArray,
      previewType,
      workflow,
      timerState,
      selectedPrelemEditState,
      gifPlaying,
      createComment,
      setEnableWorkflowHistory,
      loader,
      handleBack,
      handleChange,
      onPublishClick,
      savePage,
    ],
  );

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          {mobileHeader && !isPreviewPage && (
            <HeaderMobile
              lastmodifiedDate={page?.pageModel.Page_LastModificationDate}
              // value={previewType}
              // handleChange={handleChange}
              handleBack={handleBack}
              isSaveButtonEnabled={
                canAccessAction(CATEGORY_PAGE, "", "Create") ? isSaveButtonEnabled : false
              }
              // isPublishButtonEnabled={
              //   canAccessAction(CATEGORY_PAGE, "", "publish")
              //     ? Object.values(page?.prelemMetaArray).every((v: any) => v.IsHidden)
              //       ? false
              //       : true
              //     : false
              // }
              // previewStatus={
              //   Object.values(page?.prelemMetaArray).every((v: any) => v.IsHidden) ||
              //   page?.prelemMetaArray.length === 0
              //     ? false
              //     : true
              // }
              handleSaveClick={() => savePage()}
              handlePublishClick={() => onPublishClick()}
              timerState={timerState}
              prelemEditState={selectedPrelemEditState}
              workflow={workflow}
              setEnableWorkflowHistory={setEnableWorkflowHistory}
              loader={loader}
            />
          )}
          {enableWorkflowHistory ? (
            <Box className={classes.workflowPage}>
              <Suspense fallback={<LazyFallback />}>
                <WorkflowHistory
                  workflow={workflow}
                  setEnableWorkflowHistory={setEnableWorkflowHistory}
                />
              </Suspense>
            </Box>
          ) : (
            <Box>
              {publishLoading && <Loader />}
              {webHeader && !isPreviewPage && !contentGalleryStatus && (
                <Header {...memoizedProps} />
              )}
              <PageLayout>
                {isResetPopup ? (
                  <CommonPlateformXDialog
                    isDialogOpen={isResetPopup}
                    title={t("prelem_reset")}
                    subTitle={t("prelem_reset_title")}
                    closeButtonText={t("no")}
                    confirmButtonText={t("yes")}
                    closeButtonHandle={resetCloseButtonHandle}
                    confirmButtonHandle={resetConfirmButtonHandle}
                  />
                ) : null}
                {isDeletePopup ? (
                  <CommonPlateformXDialog
                    isDialogOpen={isDeletePopup}
                    title={t("delete_title")}
                    subTitle={`${t("delete_confirm")} ${t("prelem")}? ${t("process_undone")}`}
                    closeButtonText={t("no_keep_it")}
                    confirmButtonText={t("yes_delete_it")}
                    closeButtonHandle={deleteCloseButtonHandle}
                    confirmButtonHandle={deleteConfirmButtonHandle}
                    modalType='delete'
                  />
                ) : null}

                {/* {openPublishModal || showWorkflowSubmit ? (
              <LoadingTextModal
                isDialogOpen={openPublishModal || showWorkflowSubmit}
                closeButtonHandle={closeButtonHandle}
                subTitle={
                  openPublishModal ? t("page_loading_text") : t("requested_action_progress")
                }
                successText={openPublishModal ? t("page_success_text") : t("requested_action")}
                changeDialogState={changePublishDialogState}
              />
            ) : null} */}
                {openPublishModal || showWorkflowSubmit ? (
                  <CommonPlateformXDialog
                    isDialogOpen={openPublishModal || showWorkflowSubmit}
                    title={t("congratulations")}
                    subTitle={
                      openPublishModal
                        ? `${t("your")} ${t("page")} ${t("publish_popup_message")}`
                        : t("requested_action")
                    }
                    closeButtonHandle={closeButtonHandle}
                    confirmButtonText={t("go_to_listing")}
                    confirmButtonHandle={() => navigate("/sitepage")}
                    modalType='publish'
                  />
                ) : null}
                {showSaveWarning ? (
                  <CommonPlateformXDialog
                    isDialogOpen={showSaveWarning}
                    title={t("save_warn_title")}
                    subTitle={t("save_warn_subtitle")}
                    closeButtonText={
                      t("stay_here")
                      //triggerCase === "PUBLISH" ? t("publish_anyways") : t("stay_here")
                    }
                    confirmButtonText={t("take_me_out")}
                    closeButtonHandle={unsavedChangesCrossButtonHandle}
                    // closeButtonHandle={() => callFnsCase(triggerCase)}
                    confirmButtonHandle={() => callFnsCase(triggerCase)}
                    // crossButtonHandle={unsavedChangesCrossButtonHandle}
                    modalType='unsavedChanges'
                  />
                ) : null}
                {contentGalleryStatus && (
                  <Suspense fallback={<LazyFallback />}>
                    {ECOM_PRELEM_LIST.includes(data[currentPrelemIndx]?.PrelemId) ? (
                      <EcommerceAuthoring
                        ecomDoneClick={ecomDoneClick}
                        ecomCancelClick={onToggleContentGallery}
                        fromPageContentType={fromPageContentType}
                      />
                    ) : ["query", "multi_select", "single_select", "querywithslots"].includes(
                        data[currentPrelemIndx]?.content?.dynamic_prelem_setting?.selection_type,
                      ) ? (
                      <DynamicContentGallery
                        handleSelectedContent={(contentObj) =>
                          handleSelectedDynamicSchemaContent(
                            contentObj,
                            data[currentPrelemIndx]?.content?.dynamic_prelem_setting
                              ?.selection_type,
                          )
                        }
                        single_select={
                          data[currentPrelemIndx]?.content?.dynamic_prelem_setting
                            ?.selection_type === "single_select"
                        }
                        multi_select={[
                          "multi_select",
                          ...(DPWORLDS_TOUR_PRELEMS.includes(data[currentPrelemIndx]?.PrelemId)
                            ? ["query"]
                            : []),
                        ].includes(
                          data[currentPrelemIndx]?.content?.dynamic_prelem_setting?.selection_type,
                        )}
                        onToggleContentGallery={onToggleContentGallery}
                        prelemId={data[currentPrelemIndx]?.PrelemId}
                        allowed_collection={
                          data[currentPrelemIndx]?.content?.dynamic_prelem_setting
                            ?.allowed_collection
                        }
                        allowed_content={
                          data[currentPrelemIndx]?.content?.dynamic_prelem_setting?.allowed_content
                        }
                        selected_filter={
                          data[currentPrelemIndx]?.content?.dynamic_prelem_setting?.selected_filter
                        }
                        selectedItems={data[currentPrelemIndx]?.content?.selected_items}
                        allowEmptySelection={DPWORLDS_TOUR_PRELEMS.includes(
                          data[currentPrelemIndx]?.PrelemId,
                        )}
                      />
                    ) : MULTISLOT_PRELEM_MANUAL_SELECT.includes(
                        data[currentPrelemIndx]?.PrelemId,
                      ) ? (
                      <DynamicContentGallery
                        handleSelectedContent={handleSelectedMultiSlotContent}
                        onToggleContentGallery={onToggleContentGallery}
                        prelemId={data[currentPrelemIndx]?.PrelemId}
                        single_select={true}
                        allowed_collection={["Gallery", "Image Gallery", "Video Gallery"]}
                        allowed_content={["Articles", "Events", "Polls", "Quiz", "Video Card"]}
                        selected_filter={data[currentPrelemIndx]?.content?.selected_filter}
                      />
                    ) : (
                      //This is used in basic button flow for content type addition. To remove this, we need to use a different state to open this pop up, which will help us identify where the click came from.
                      <ContentGallery
                        fromPageContentType={fromPageContentType}
                        handleSelectedContent={handleSelectedContent}
                        onToggleContentGallery={onToggleContentGallery}
                        contentType={
                          (data[currentPrelemIndx]?.content?.PrelemContentType?.length === 1 &&
                            data[currentPrelemIndx]?.content?.PrelemContentType[0] === "Select") ||
                          data[currentPrelemIndx]?.content?.PrelemContentType?.length === 0 ||
                          data[currentPrelemIndx]?.content?.PrelemContentType === undefined ||
                          data[currentPrelemIndx]?.content?.PrelemContentType === null
                            ? ["Article", "Quiz", "Vod", "Poll", "Event"]
                            : data[currentPrelemIndx]?.content?.PrelemContentType
                        }
                      />
                    )}
                  </Suspense>
                )}
                {customContentGalleryStatus && (
                  <Suspense fallback={<LazyFallback />}>
                    <CustomContentGallery
                      selectedPrelemIndex={currentPrelemIndx}
                      isOpen={customContentGalleryStatus}
                      onClose={() => onToggleCustomContent(false)}
                      type={
                        data[currentPrelemIndx]?.content?.dynamic_prelem_setting?.allowed_content
                      }
                    />
                  </Suspense>
                )}
                {!isPreviewPage && (
                  <LeftBox>
                    {/* Page Settings */}
                    <Box className={classes.pageSettingBox}>
                      {pageId === PageSettingListData?.[0]?.id && (
                        <PageInfo setPageId={setPageId} />
                      )}
                      {pageId === PageSettingListData?.[1]?.id && (
                        <SEOBasics setPageId={setPageId} />
                      )}
                      {pageId === PageSettingListData?.[2]?.id && (
                        <SocialShare setPageId={setPageId} />
                      )}
                      {pageId === PageSettingListData?.[3]?.id && (
                        <Analytics setPageId={setPageId} />
                      )}
                      {pageId === PageSettingListData?.[4]?.id && (
                        <Schedule setPageId={setPageId} />
                      )}

                      {/* Prelem Settings */}
                      {PrelemSettingCardList?.includes(pageId) && (
                        <PrelemSettingsCard
                          selectedPrelemIndex={prelemIndex}
                          pageId={pageId}
                          setPageId={setPageId}
                          updatePrelemContent={updatePrelemContent}
                          imageSettingRef={imageSettingRef}
                        />
                      )}
                    </Box>

                    {pageId === "pageSetting" && (
                      <Box className={classes.PageSettingMenuTabs}>
                        <TabContext value={value}>
                          <Box
                            className={classes.tabButtonsBottom}
                            sx={{ borderBottom: 1, borderColor: "divider" }}>
                            {isMobileView && (
                              <TabList
                                TabIndicatorProps={{
                                  style: { display: "none" },
                                }}
                                className={classes.TabButtons}
                                onChange={handleChangeTab}
                                aria-label='lab API tabs example'>
                                (
                                <Tab
                                  icon={<img src={EditIcon} alt='icon' />}
                                  iconPosition='start'
                                  label={t("design")}
                                  value='Page_Design'
                                />
                                )
                                {isMobileView && (
                                  <Tab
                                    icon={<img src={SettingIcon} alt='icon' />}
                                    iconPosition='start'
                                    label={t("page_setting")}
                                    value='Page_Setting'
                                  />
                                )}
                              </TabList>
                            )}

                            {!isMobileView && (
                              <Typography variant='h5semibold'>{t("page_setting")}</Typography>
                            )}
                          </Box>
                          {isMobileView && (
                            <TabPanel
                              sx={{ padding: 0 }}
                              className={classes.tabPanelSettingPage}
                              value='Page_Design'>
                              {loader ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: "calc(100vh - 250px)",
                                    width: "100%",
                                  }}>
                                  <CircularProgress
                                    sx={{ color: theme?.palette?.customColor?.SECONDARY }}
                                  />
                                </Box>
                              ) : page?.prelemMetaArray.length > 0 ? (
                                <Box
                                  className={classes.addEditPrelemBox}
                                  sx={{
                                    overflowY: selectedPrelemEditState
                                      ? { xs: "auto", em: "hidden" }
                                      : "auto",
                                  }}>
                                  <Box className={classes.innerBoxWeb}>
                                    {page?.prelemMetaArray?.map((prelemData: PrelemInstance, i) => {
                                      const showIconsState = {
                                        showCreate: true,
                                        showVisible: prelemData.IsHidden,
                                        showCopy: true,
                                        showUp: i === 0 ? false : true,
                                        showDown:
                                          i === page?.prelemMetaArray.length - 1 ? false : true,
                                        showReset: prelemData.IsModified,
                                        showDelete: true,
                                        showSettings: true,
                                      };
                                      const showAddSection = {
                                        showAtTop: true,
                                        showAtBottom:
                                          i === page?.prelemMetaArray.length - 1 ? true : false,
                                      };
                                      return (
                                        <Prelem
                                          onOpenContentType={onOpenContentType}
                                          count={count++}
                                          key={`card${i}`}
                                          imageSettingRef={imageSettingRef}
                                          prelemEditState={
                                            selectedPrelemEditMode === i
                                              ? selectedPrelemEditState
                                              : false
                                          }
                                          showIconsState={showIconsState}
                                          prelemData={prelemData}
                                          index={i}
                                          handleOperationClick={handleOperations}
                                          setPageId={setPageId}
                                          onToggleContentGallery={onToggleContentGallery}
                                          contentGalleryStatus={contentGalleryStatus}
                                          eComContentGalleryHandle={eComContentGalleryHandle}
                                          handlePrelemEditSubmit={(
                                            schemaArray,
                                            prelemRef,
                                            index,
                                            buttonsKeysPopulatedObj,
                                          ) => {
                                            setSaveDone(true);
                                            handleSetSelectedPrelemEditState(
                                              schemaArray,
                                              prelemRef,
                                              index,
                                              buttonsKeysPopulatedObj,
                                            );
                                          }}
                                          saveDone={saveDone}
                                          selectedContentForButton={
                                            selectedContentEditorialPath.current
                                          }
                                          prelemDataReset={
                                            prelemResetIndex === i ? prelemDataReset : false
                                          }
                                          showAddSection={showAddSection}
                                          addSectionTouchPointClick={addSectionTouchPointClick}
                                          onToggleCustomContent={onToggleCustomContent}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              ) : (
                                <Box
                                  component='div'
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: {
                                      xs: "320px",
                                      sm: "320px",
                                      md: "320px",
                                      lg: "512px",
                                    },
                                    backgroundColor: "#ffffff",
                                    position: "relative",
                                  }}>
                                  <Box className={classes.emptWpBoxInner}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}>
                                      <EditPageIcons
                                        className='EditPageIcons'
                                        nameIcon='add'
                                        enable
                                        listIndx='top'
                                        handleClick={() => routeChange()}
                                      />
                                    </Box>
                                    <Typography variant='p3regular' mb='10px' mt='10px'>
                                      {t("create_first_section_now")}
                                    </Typography>
                                    <Typography
                                      sx={{ cursor: "pointer" }}
                                      color='activeColor'
                                      onClick={() => routeChange()}>
                                      {t("add_prelem")}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </TabPanel>
                          )}
                          <TabPanel
                            sx={{ padding: 0 }}
                            className={classes.tabPanelSettingPage}
                            value='Page_Setting'>
                            <PageSettingList setPageId={setPageId} />
                          </TabPanel>
                        </TabContext>
                      </Box>
                    )}
                    {pageId === "prelemSetting" && (
                      <PrelemSettingMenu setPageId={setPageId} selectedPrelemIndex={prelemIndex} />
                    )}
                    {pageId === "prelemInfo" && prelemIndex !== -1 && (
                      <PrelemInfo setPageId={setPageId} selectedPrelemIndex={prelemIndex} />
                    )}
                  </LeftBox>
                )}
                <RightBox>
                  {loader ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "calc(100vh - 250px)",
                        width: "100%",
                      }}>
                      <CircularProgress sx={{ color: theme?.palette?.customColor?.SECONDARY }} />
                    </Box>
                  ) : page?.prelemMetaArray.length > 0 ? (
                    <Box
                      className={`${classes.addEditPrelemBox} ${isPreviewPage && classes.previewPage}`}
                      sx={{
                        overflowY: selectedPrelemEditState ? "hidden" : "scroll",
                      }}>
                      <Box className={classes.innerBoxWeb}>
                        {page?.prelemMetaArray?.map((prelemData: PrelemInstance, i) => {
                          const showIconsState = {
                            showCreate: true,
                            showVisible: prelemData.IsHidden,
                            showCopy: true,
                            showUp: i === 0 ? false : true,
                            showDown: i === page?.prelemMetaArray.length - 1 ? false : true,
                            showReset: prelemData.IsModified,
                            showDelete: true,
                            showSettings: true,
                          };
                          const showAddSection = {
                            showAtTop: true,
                            showAtBottom: i === page?.prelemMetaArray.length - 1 ? true : false,
                          };
                          return (
                            <Prelem
                              onOpenContentType={onOpenContentType}
                              count={count++}
                              key={`card${i}`}
                              imageSettingRef={imageSettingRef}
                              prelemEditState={
                                selectedPrelemEditMode === i ? selectedPrelemEditState : false
                              }
                              showIconsState={showIconsState}
                              prelemData={prelemData}
                              index={i}
                              handleOperationClick={handleOperations}
                              setPageId={setPageId}
                              onToggleContentGallery={onToggleContentGallery}
                              contentGalleryStatus={contentGalleryStatus}
                              eComContentGalleryHandle={eComContentGalleryHandle}
                              handlePrelemEditSubmit={(
                                schemaArray,
                                prelemRef,
                                index,
                                buttonsKeysPopulatedObj,
                              ) => {
                                setSaveDone(true);
                                handleSetSelectedPrelemEditState(
                                  schemaArray,
                                  prelemRef,
                                  index,
                                  buttonsKeysPopulatedObj,
                                );
                              }}
                              saveDone={saveDone}
                              selectedContentForButton={selectedContentEditorialPath.current}
                              prelemDataReset={prelemResetIndex === i ? prelemDataReset : false}
                              showAddSection={showAddSection}
                              addSectionTouchPointClick={addSectionTouchPointClick}
                              isPreviewPage={isPreviewPage}
                              onToggleCustomContent={onToggleCustomContent}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      component='div'
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: {
                          xs: "320px",
                          sm: "320px",
                          md: "320px",
                          lg: "512px",
                        },
                        backgroundColor: "#ffffff",
                        position: "relative",
                      }}>
                      {!isPreviewPage && (
                        <Box className={classes.emptWpBoxInner}>
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <EditPageIcons
                              styleObject={{
                                color: "#4B9EF9",
                                display: "block",
                                padding: 0,
                                maxHeight: "50px",
                                maxWidth: "50px",
                                "& svg": {
                                  fontSize: "50px",
                                },
                              }}
                              nameIcon='add'
                              enable
                              listIndx='top'
                              handleClick={() => routeChange()}
                            />
                          </Box>
                          <Typography variant='p3regular' mb='10px' mt='10px'>
                            {t("create_first_section_now")}
                          </Typography>
                          <Typography
                            variant='p3regular'
                            sx={{ color: "#4B9EF9", cursor: "pointer" }}
                            onClick={() => routeChange()}>
                            {t("add_prelem")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </RightBox>
              </PageLayout>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default EditPageContainer;
